import * as THREE from 'three';
import type { ReferencePlane } from '../../../../../reference/ReferencePlane';
import type { ParabolaProjectionResult, WorldCoordinate } from '../../../model/ProjectionResult';
import type { ResolvedParabolaReferenceSource } from '../SketchReferenceSourceResolver';
import {
	PROJECTION_EPSILON,
	toWorldCoordinate,
} from './SupportProjectionPreprocessor';
import type { SupportProjectionPreprocessor } from './SupportProjectionPreprocessor';
import {
	hasUsableCurveExtent,
	projectWorldCoordinatesToPlane,
} from './SplineSupportProjectionPreprocessor';

/**
 * 抛物线源投影预处理器。
 * 说明：
 * 1. 当前抛物线对象以焦点、顶点和起终参数表达语义；
 * 2. 同平面或平行平面转换时，正交投影后的焦点/顶点与参数可以完整复用；
 * 3. 采样曲线点用于 preview 与端点在曲线上拖拽时的吸附。
 */
export class ParabolaSupportProjectionPreprocessor
	implements SupportProjectionPreprocessor<ResolvedParabolaReferenceSource, ParabolaProjectionResult>
{
	/**
	 * 执行抛物线投影。
	 *
	 * @param input 投影输入。
	 * @returns 成功时返回 parabola，退化时返回 null。
	 */
	public projectToTargetPlane(input: {
		source: ResolvedParabolaReferenceSource;
		targetReferencePlane: ReferencePlane;
	}): ParabolaProjectionResult | null {
		const focalWorld = projectWorldCoordinateToPlane(
			input.source.focalWorld,
			input.targetReferencePlane,
		);
		const apexWorld = projectWorldCoordinateToPlane(
			input.source.apexWorld,
			input.targetReferencePlane,
		);
		const curvePointWorlds = projectWorldCoordinatesToPlane(
			input.source.curvePointWorlds,
			input.targetReferencePlane,
		);

		if (
			new THREE.Vector3(...focalWorld).distanceTo(new THREE.Vector3(...apexWorld)) <= PROJECTION_EPSILON
			|| !hasUsableCurveExtent(curvePointWorlds)
		) {
			return null;
		}

		return {
			sourceDescriptor: input.source.sourceDescriptor,
			sourceKind: 'parabola',
			projectionResultKind: 'parabola',
			diagnostics: [],
			geometryPayload: {
				focalWorld,
				apexWorld,
				startParameter: input.source.startParameter,
				endParameter: input.source.endParameter,
				segments: input.source.segments,
				curvePointWorlds,
			},
		};
	}
}

/**
 * 将世界坐标点正交投影到目标参考面。
 *
 * @param pointWorld 输入世界坐标。
 * @param targetReferencePlane 目标参考面。
 * @returns 投影后的世界坐标。
 */
function projectWorldCoordinateToPlane(
	pointWorld: WorldCoordinate,
	targetReferencePlane: ReferencePlane,
): WorldCoordinate {
	const localPoint = targetReferencePlane.worldToLocal(new THREE.Vector3(...pointWorld));
	return toWorldCoordinate(targetReferencePlane.localToWorld(localPoint));
}
